import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  TextField,
  Slide,
  LinearProgress
} from '@mui/material';
import {
  Mic as MicIcon,
  Stop as StopIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
  Psychology as PsychologyIcon,
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon
} from '@mui/icons-material';

const API_URL = '/api';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const TranscripcionConsulta = ({
  open,
  onClose,
  consultaId,
  userId,
  onTranscripcionComplete
}) => {
  const [grabando, setGrabando] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [reproduciendo, setReproduciendo] = useState(false);
  const [transcribiendo, setTranscribiendo] = useState(false);
  const [transcripcion, setTranscripcion] = useState('');
  const [notaSoap, setNotaSoap] = useState(null);
  const [error, setError] = useState(null);
  const [tiempoGrabacion, setTiempoGrabacion] = useState(0);
  const [copiado, setCopiado] = useState(false);

  const mediaRecorderRef = useRef(null);
  const audioRef = useRef(null);
  const timerRef = useRef(null);
  const chunksRef = useRef([]);

  // Limpiar al cerrar
  useEffect(() => {
    if (!open) {
      limpiarGrabacion();
    }
  }, [open]);

  // Timer para grabación
  useEffect(() => {
    if (grabando) {
      timerRef.current = setInterval(() => {
        setTiempoGrabacion(prev => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [grabando]);

  const formatearTiempo = (segundos) => {
    const mins = Math.floor(segundos / 60);
    const secs = segundos % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const iniciarGrabacion = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });

      chunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start(1000);
      setGrabando(true);
      setTiempoGrabacion(0);
      setError(null);
    } catch (err) {
      console.error('Error al acceder al micrófono:', err);
      setError('No se pudo acceder al micrófono. Verifica los permisos.');
    }
  };

  const detenerGrabacion = () => {
    if (mediaRecorderRef.current && grabando) {
      mediaRecorderRef.current.stop();
      setGrabando(false);
    }
  };

  const limpiarGrabacion = () => {
    setAudioBlob(null);
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioUrl(null);
    setTranscripcion('');
    setNotaSoap(null);
    setTiempoGrabacion(0);
    setError(null);
  };

  const toggleReproduccion = () => {
    if (audioRef.current) {
      if (reproduciendo) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setReproduciendo(!reproduciendo);
    }
  };

  const transcribirAudio = async () => {
    if (!audioBlob) return;

    setTranscribiendo(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'grabacion.webm');
      if (consultaId) {
        formData.append('consulta_id', consultaId);
      }
      formData.append('generar_soap', 'true');

      const res = await fetch(`${API_URL}/emr/ia/transcribir`, {
        method: 'POST',
        headers: {
          'X-User-Id': userId
        },
        body: formData
      });

      if (!res.ok) {
        throw new Error('Error al transcribir el audio');
      }

      const data = await res.json();
      setTranscripcion(data.transcripcion_raw || '');
      setNotaSoap(data.nota_soap || null);

      if (onTranscripcionComplete) {
        onTranscripcionComplete(data);
      }
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
    } finally {
      setTranscribiendo(false);
    }
  };

  const copiarTexto = (texto) => {
    navigator.clipboard.writeText(texto);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      TransitionComponent={Transition}
    >
      <DialogTitle sx={{
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        color: '#fff',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MicIcon />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Transcripción de Consulta
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#fff' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Sección de grabación */}
        <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#f8fafc', mb: 3 }}>
          <Typography variant="h3" sx={{ fontFamily: 'monospace', mb: 2, color: grabando ? '#ef4444' : '#1e3a5f' }}>
            {formatearTiempo(tiempoGrabacion)}
          </Typography>

          {grabando && (
            <LinearProgress color="error" sx={{ mb: 2, borderRadius: 2 }} />
          )}

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            {!grabando && !audioBlob && (
              <Button
                variant="contained"
                size="large"
                startIcon={<MicIcon />}
                onClick={iniciarGrabacion}
                sx={{
                  bgcolor: '#ef4444',
                  borderRadius: '50px',
                  px: 4,
                  '&:hover': { bgcolor: '#dc2626' }
                }}
              >
                Iniciar Grabación
              </Button>
            )}

            {grabando && (
              <Button
                variant="contained"
                size="large"
                startIcon={<StopIcon />}
                onClick={detenerGrabacion}
                sx={{
                  bgcolor: '#1e3a5f',
                  borderRadius: '50px',
                  px: 4
                }}
              >
                Detener
              </Button>
            )}

            {audioBlob && !grabando && (
              <>
                <IconButton
                  onClick={toggleReproduccion}
                  sx={{
                    bgcolor: '#10b981',
                    color: '#fff',
                    '&:hover': { bgcolor: '#059669' }
                  }}
                >
                  {reproduciendo ? <PauseIcon /> : <PlayIcon />}
                </IconButton>
                <IconButton
                  onClick={limpiarGrabacion}
                  sx={{
                    bgcolor: '#ef4444',
                    color: '#fff',
                    '&:hover': { bgcolor: '#dc2626' }
                  }}
                >
                  <DeleteIcon />
                </IconButton>
                <Button
                  variant="contained"
                  startIcon={transcribiendo ? <CircularProgress size={20} color="inherit" /> : <PsychologyIcon />}
                  onClick={transcribirAudio}
                  disabled={transcribiendo}
                  sx={{ bgcolor: '#6366f1', borderRadius: '50px', px: 3 }}
                >
                  {transcribiendo ? 'Transcribiendo...' : 'Transcribir con IA'}
                </Button>
              </>
            )}
          </Box>

          {audioUrl && (
            <audio
              ref={audioRef}
              src={audioUrl}
              onEnded={() => setReproduciendo(false)}
              style={{ display: 'none' }}
            />
          )}
        </Paper>

        {/* Transcripción */}
        {transcripcion && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Transcripción
              </Typography>
              <IconButton onClick={() => copiarTexto(transcripcion)} size="small">
                {copiado ? <CheckIcon color="success" /> : <CopyIcon />}
              </IconButton>
            </Box>
            <TextField
              fullWidth
              multiline
              rows={6}
              value={transcripcion}
              onChange={(e) => setTranscripcion(e.target.value)}
              variant="outlined"
            />
          </Box>
        )}

        {/* Nota SOAP generada por IA */}
        {notaSoap && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <PsychologyIcon color="primary" />
              Nota SOAP Generada
            </Typography>

            <Alert severity="info" sx={{ mb: 2 }}>
              Esta nota fue generada automáticamente por IA. Revise y edite según sea necesario.
            </Alert>

            <Paper sx={{ p: 2, bgcolor: '#f0fdf4' }}>
              {notaSoap.subjetivo && (
                <Box sx={{ mb: 2 }}>
                  <Chip label="S - Subjetivo" size="small" color="primary" sx={{ mb: 1 }} />
                  <Typography variant="body2">{notaSoap.subjetivo}</Typography>
                </Box>
              )}
              {notaSoap.objetivo && (
                <Box sx={{ mb: 2 }}>
                  <Chip label="O - Objetivo" size="small" color="secondary" sx={{ mb: 1 }} />
                  <Typography variant="body2">{notaSoap.objetivo}</Typography>
                </Box>
              )}
              {notaSoap.analisis && (
                <Box sx={{ mb: 2 }}>
                  <Chip label="A - Análisis" size="small" color="warning" sx={{ mb: 1 }} />
                  <Typography variant="body2">{notaSoap.analisis}</Typography>
                </Box>
              )}
              {notaSoap.plan && (
                <Box sx={{ mb: 2 }}>
                  <Chip label="P - Plan" size="small" color="success" sx={{ mb: 1 }} />
                  <Typography variant="body2">{notaSoap.plan}</Typography>
                </Box>
              )}

              {notaSoap.sintomas_detectados?.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">Síntomas detectados:</Typography>
                  <Box sx={{ mt: 0.5 }}>
                    {notaSoap.sintomas_detectados.map((s, i) => (
                      <Chip key={i} label={s} size="small" sx={{ m: 0.25 }} />
                    ))}
                  </Box>
                </Box>
              )}

              {notaSoap.posibles_diagnosticos?.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">Posibles diagnósticos:</Typography>
                  <Box sx={{ mt: 0.5 }}>
                    {notaSoap.posibles_diagnosticos.map((d, i) => (
                      <Chip key={i} label={d} size="small" color="info" sx={{ m: 0.25 }} />
                    ))}
                  </Box>
                </Box>
              )}
            </Paper>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid #e2e8f0' }}>
        <Button onClick={onClose} color="inherit">
          Cerrar
        </Button>
        {transcripcion && (
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={() => {
              if (onTranscripcionComplete) {
                onTranscripcionComplete({ transcripcion_raw: transcripcion, nota_soap: notaSoap });
              }
              onClose();
            }}
          >
            Usar en Consulta
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TranscripcionConsulta;
